import { useEffect, useState, useMemo } from 'react';
import {
  ApiModelVersionDataPoint,
  useModelVersionsQuery,
} from 'src/api/queries';
import { AppServices } from 'src/app/routes/utils';
import { collapsePairs } from 'src/utils/modelPairs';
import { sortByAlias } from 'src/utils/sort';
import { useAppContext } from '../AppContext';

type PairableModelVersion = {
  alias: string;
  modelName?: string;
  quickRun?: boolean;
};

// Structural fields from the API when available; alias parsing only as a
// fallback for responses that predate modelName/quickRun (deploy skew,
// cached queries).
export const isQuickVersion = (mv: PairableModelVersion): boolean =>
  mv.quickRun ?? mv.alias.endsWith('(Quick)');

export const baseModelName = (mv: PairableModelVersion): string =>
  mv.modelName ?? mv.alias.replace(/ \((?:Quick|Full)\)$/, '');

const versionPairKey = baseModelName;

// Everything in the model-versions list is published (i.e. its run
// succeeded), so a Full version existing at all means its Quick preview is
// redundant — the same "both active" rule the Settings Models tab applies.
export const filterQuickWhenFullExists = <T extends PairableModelVersion>(
  modelVersions: T[],
): T[] =>
  collapsePairs(modelVersions, {
    isQuick: isQuickVersion,
    pairKey: versionPairKey,
  });

export type ModelVersion = ApiModelVersionDataPoint & { customerName: string };

export type ModelVersionsState = {
  modelVersions: Array<ModelVersion>;
  isLoading: boolean;
  isError: boolean;
};

const INIT_MODEL_VERSIONS_STATE: Readonly<ModelVersionsState> = {
  modelVersions: [],
  isError: false,
  isLoading: false,
};

export const useModelVersions = (
  currentAppService: AppServices | null,
  currentModelVersion: ModelVersion | null,
  setCurrentModelVersion: (modelVersion: ModelVersion | null) => void,
  updateTimeRangeFromModel: (modelVersion: ModelVersion) => void,
  timeRangeLocked: boolean,
) => {
  const { currentCustomer, subscriptionLevels } = useAppContext();

  const [modelVersionsState, setAllModelVersionsState] =
    useState<ModelVersionsState>(INIT_MODEL_VERSIONS_STATE);

  const enabled =
    !!currentCustomer &&
    !!currentAppService &&
    !!subscriptionLevels[currentAppService];

  const hasNoModelVersions = modelVersionsState.modelVersions.length === 0;

  const {
    data: mvData,
    isLoading: mvLoading,
    isError: mvError,
  } = useModelVersionsQuery(
    {
      customer: currentCustomer?.customerName ?? 'missing_customer',
      model_type: currentAppService ?? ('missing_model_Type' as AppServices),
    },
    enabled,
    hasNoModelVersions ? 5000 : false,
  );

  const filteredMvData = useMemo(
    () => filterQuickWhenFullExists(mvData ?? []),
    [mvData],
  );

  // After data has been fetched, update the context for each:
  useEffect(() => {
    if (!enabled) {
      setAllModelVersionsState(INIT_MODEL_VERSIONS_STATE);
      setCurrentModelVersion(null);
      return;
    }

    const modelVersions = filteredMvData.map((d) => ({
      ...d,
      customerName: currentCustomer.customerName,
    }));

    setAllModelVersionsState({
      modelVersions: sortByAlias(modelVersions),
      isLoading: mvLoading,
      isError: mvError,
    });

    if (modelVersions.length > 0) {
      // Only set model if there's no current model or current model is not in the list
      const currentModelInList = currentModelVersion
        ? modelVersions.find(
            (m) =>
              m.modelParametersId === currentModelVersion.modelParametersId,
          )
        : null;

      if (!currentModelInList) {
        const newModelVersion = modelVersions[0];
        setCurrentModelVersion(newModelVersion);
        if (!timeRangeLocked) {
          updateTimeRangeFromModel(newModelVersion);
        }
      }
    } else {
      setCurrentModelVersion(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    filteredMvData,
    mvLoading,
    mvError,
    currentCustomer,
    setCurrentModelVersion,
    updateTimeRangeFromModel,
  ]);

  return {
    modelVersionsState,
    setCurrentModelVersion,
  };
};
